import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import i18n from 'i18next';

import useApplyNewStore from "../../store/useApplyNewStore";
import useModalStore from "../../store/useModalStore";
import useViewStore from "../../store/useViewStore";
import LanguageSwitcher from "./LanguageSwitcher";
import FormButton from "../form/FormButton";
import NavStepControl from "./NavStepControl";
import { basename } from "../../setting";

const NavigationBarApply = () => {
  const { step, setStep, resetStep } = useApplyNewStore();
  const { saveFormStep1, saveFormStep2, saveFormStep3, getPreAuthFormForStep4 } = useApplyNewStore();
  const { isStep1Valid, step2_upload_option1, step2_upload_option2, isStep3Valid } = useApplyNewStore();
  const { openModalConfirm } = useModalStore();
  const { isPadView, isMobileView } = useViewStore((state) => state);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const saveForm = async () => {
    if (step === "1") {
      await saveFormStep1();
    } else if (step === "2") {
      await saveFormStep2();
    } else if (step === "3") {
      await saveFormStep3();
    }
  }

  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  return (
    <Header $isMobileView={isMobileView} $isVisible={isVisible}>
      <HeaderLeft $isMobileView={isMobileView}>
        <Icon src={`${process.env.PUBLIC_URL}/images/icon.png`} alt="Blue Cross Logo" />

        {
          isPadView ?
            <NavStepControl />
            :
            <>
              <SiteTitle>{t("term.preAuth")}</SiteTitle>
              <VDivider />

              <NavItems>
                <NavItemButton onClick={async () => {
                  await saveForm();
                  setStep("1");
                }}>
                  <NavStepActive>1</NavStepActive>
                  <NavTitleActive>{t("translation:apply.nav.Step1Title")}</NavTitleActive>
                </NavItemButton>
                <HDivider />
                <NavItemButton $disabled={!isStep1Valid} onClick={async () => {
                  if (isStep1Valid) {
                    await saveForm();
                    setStep("2");
                  }
                }}>
                  {
                    isInStep2OrAfter(step) ?
                      <>
                        <NavStepActive>2</NavStepActive>
                        <NavTitleActive>{t("translation:apply.nav.Step2Title")}</NavTitleActive>
                      </>
                      :
                      <>
                        <NavStep>2</NavStep>
                        <NavTitle>{t("translation:apply.nav.Step2Title")}</NavTitle>
                      </>
                  }
                </NavItemButton>
                <HDivider />
                <NavItemButton $disabled={!step2_upload_option1 && !step2_upload_option2} onClick={async () => {
                  if (!!step2_upload_option1 || !!step2_upload_option2) {
                    await saveForm();
                    setStep("3");
                  }
                }}>
                  {
                    isInStep3OrAfter(step) ?
                      <>
                        <NavStepActive>3</NavStepActive>
                        <NavTitleActive>{t("translation:apply.nav.Step3Title")}</NavTitleActive>
                      </>
                      :
                      <>
                        <NavStep>3</NavStep>
                        <NavTitle>{t("translation:apply.nav.Step3Title")}</NavTitle>
                      </>
                  }
                </NavItemButton>
                <HDivider />
                <NavItemButton $disabled={!isStep3Valid} onClick={async () => {
                  if (isStep3Valid) {
                    await saveForm();
                    await getPreAuthFormForStep4();
                    setStep("4");
                  }
                }}>
                  {
                    isInStep4OrAfter(step) ?
                      <>
                        <NavStepActive>4</NavStepActive>
                        <NavTitleActive>{t("translation:apply.nav.Step4Title")}</NavTitleActive>
                      </>
                      :
                      <>
                        <NavStep>4</NavStep>
                        <NavTitle>{t("translation:apply.nav.Step4Title")}</NavTitle>
                      </>
                  }
                </NavItemButton>

              </NavItems>
            </>
        }

      </HeaderLeft>
      <HeaderRight $isMobileView={isMobileView}>
        <LanguageSwitcher />

        <FormButton onClick={() => {
          openModalConfirm({
            title: t("translation:apply.nav.exitTitle"),
            text: t("translation:apply.nav.exitContent"),
            callback: () => {
              resetStep();
              navigate(`${process.env.PUBLIC_URL}/${i18n.language}`, { replace: true });
            }
          });
        }}>
          <CloseImg src={`${process.env.PUBLIC_URL}/images/close.svg`} alt="close" />
        </FormButton>
      </HeaderRight>
    </Header>
  );
}

const isInStep2OrAfter = (step) => {
  return step === "2" || step === "3" || step === "4" || step === "success";
}

const isInStep3OrAfter = (step) => {
  return step === "3" || step === "4" || step === "success";
}

const isInStep4OrAfter = (step) => {
  return step === "4" || step === "success";
}


const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0%;
  transform: translateX(-50%);
  width: 100%;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  background-color: var(--aia-bx-white);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 5rem;
  z-index: 500;

  transition: transform 0.3s ease;
  ${props => props.$isVisible ? `transform: translateY(0);` : `transform: translateY(-100%);`}
  ${props => props.$isMobileView ? `padding: 0; gap: 10px;` : `padding: 0 16px; gap: 20px;`}
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex: 1;

  ${props => props.$isMobileView ? `gap: 3px;` : `gap: 8px;`}
`;

const Icon = styled.img`
  padding: 15px;
  aspect-ratio: 1;
`;

const SiteTitle = styled.div`
  color: var(--aia-bx-dark-blue);
  font-weight: 700;
  font-size: 20px;
  line-height: 150%;
`;

const VDivider = styled.div`
  background-color: var(--aia-bx-grey-20);
  width: 1px;
  height: 30px;
  margin-left: 15px;
  margin-right: 10px;
`;

const HDivider = styled.div`
  background-color: var(--aia-bx-grey-20);
  width: 30px;
  height: 1px;
`;

const NavControl = styled.button`
  border-radius: 50px;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--aia-bx-grey-20);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.3);
  background-color: var(--aia-bx-gray-6);

  &:hover {
    background-color: var(--aia-bx-bright-blue-5);
  }
`;

const NavItems = styled.nav`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const NavItemButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 6px;
  border-radius: 10px;

  &:hover {
    ${props => props.$disabled ? `cursor: not-allowed;` : `background-color: var(--aia-bx-bright-blue-5);`}
  }
`;

const NavStep = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--aia-bx-gray-5);
  color: var(--aia-bx-white);
  height: 32px;
  width: 32px;
  line-height: 0px;
  border-radius: 50%;
  flex: none;
`;

const NavStepActive = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--aia-bx-blue-2);
  color: var(--aia-bx-white);
  height: 32px;
  width: 32px;
  line-height: 0px;
  border-radius: 50%;
  flex: none;
`;

const NavTitle = styled.div`
  margin-left: 10px;
  font-size: 17px;
  font-weight: bold;
  color: var(--aia-bx-gray-50);
`;

const NavTitleActive = styled.div`
  margin-left: 10px;
  font-size: 17px;
  font-weight: bold;
  color: var(--aia-bx-dark-blue);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;

  ${props => props.$isMobileView ? `gap: 0px;` : `gap: 8px;`}
`;

const CloseImg = styled.img`
  width: 30px;
  height: 30px;
`;

export default NavigationBarApply;